import { useState } from 'react';
import Header from './components/Header';
import SubjectSelector from './components/SubjectSelector';
import ChatWindow from './components/ChatWindow';
import ResourcesPanel from './components/KnowledgePanel';
import SubjectLockScreen from './components/SubjectLockScreen';
import SharedContentStyles from './components/SharedContentStyles';
import { getSubjectByName } from '../../config/subjects';

function HomeStyles() {
  return (
    <style>{`
      .minerva-app {
        font-family: var(--font-ui);
        background-color: var(--ink-900);
        color: var(--vellum);
        position: relative;
      }
      /* Vignette sutil: la luz cae hacia el centro, como un atril iluminado */
      .minerva-app::before {
        content: '';
        position: fixed;
        inset: 0;
        background:
          radial-gradient(120% 90% at 50% 0%, rgba(194,162,78,0.05), transparent 55%),
          radial-gradient(140% 120% at 50% 120%, var(--ink-850), transparent 60%);
        pointer-events: none;
        z-index: 0;
      }
      .app-grain-overlay {
        position: fixed;
        inset: 0;
        background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='250' height='250'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='250' height='250' filter='url(%23n)' opacity='1'/%3E%3C/svg%3E");
        opacity: 0.04;
        pointer-events: none;
        z-index: 999;
        mix-blend-mode: overlay;
      }
      @keyframes app-fade-in {
        from { opacity: 0; transform: translateY(16px); }
        to   { opacity: 1; transform: translateY(0); }
      }
      .app-panel-enter {
        animation: app-fade-in 0.45s cubic-bezier(0.16, 1, 0.3, 1) both;
      }
    `}</style>
  );
}

export default function Home() {
  const [selectedSubject, setSelectedSubject] = useState<string | null>(null);
  const [pendingLockedSubject, setPendingLockedSubject] = useState<string | null>(null);
  const [unlockedSubjects, setUnlockedSubjects] = useState<Record<string, boolean>>({});
  const [showResourcesPanel, setShowResourcesPanel] = useState(true);

  const handleSubjectSelect = (subject: string) => {
    const subjectConfig = getSubjectByName(subject);
    const subjectId = subjectConfig?.id;
    const hasPassword = Boolean(subjectConfig?.accessPassword);
    const isUnlocked = subjectId ? unlockedSubjects[subjectId] === true : false;

    if (hasPassword && !isUnlocked) {
      setPendingLockedSubject(subject);
      setSelectedSubject(null);
      return;
    }

    setPendingLockedSubject(null);
    setSelectedSubject(subject);
  };

  const handleUnlockSubject = (password: string): boolean => {
    if (!pendingLockedSubject) return false;

    const subjectConfig = getSubjectByName(pendingLockedSubject);
    if (!subjectConfig?.accessPassword || subjectConfig.accessPassword !== password) {
      return false;
    }

    setUnlockedSubjects((prev) => ({ ...prev, [subjectConfig.id]: true }));
    setSelectedSubject(pendingLockedSubject);
    setPendingLockedSubject(null);
    return true;
  };

  const handleBackToSubjects = () => {
    setSelectedSubject(null);
    setPendingLockedSubject(null);
  };

  const subjectShownInHeader = selectedSubject ?? pendingLockedSubject;

  return (
    <>
      <HomeStyles />
      <SharedContentStyles />
      <div className="app-grain-overlay" />
      <div className="minerva-app flex h-[100dvh] overflow-hidden">
        <div className="flex-1 flex flex-col min-w-0" style={{ position: 'relative', zIndex: 1 }}>
          <Header
            selectedSubject={subjectShownInHeader}
            onBackToSubjects={handleBackToSubjects}
            onToggleResourcesPanel={() => setShowResourcesPanel(!showResourcesPanel)}
            showResourcesPanel={showResourcesPanel}
          />

          <div className="flex-1 flex overflow-hidden">
            {!selectedSubject && !pendingLockedSubject ? (
              <SubjectSelector onSelectSubject={handleSubjectSelect} />
            ) : pendingLockedSubject ? (
              <SubjectLockScreen
                subjectName={pendingLockedSubject}
                onUnlock={handleUnlockSubject}
                onBack={handleBackToSubjects}
              />
            ) : (
              <>
                <div style={{ display: 'flex', flex: 1, minWidth: 0 }}>
                  <ChatWindow subject={selectedSubject} conversationId={null} />
                </div>
                {showResourcesPanel && <ResourcesPanel subject={selectedSubject} />}
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
